import React, { useState, useEffect } from 'react';
import './CategoryProductManager.css';

const CategoryProductManager = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Formulaires inline (pas de modaux)
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });
  const [productForm, setProductForm] = useState({ 
    name: '', 
    category: '', 
    unit: 'PIECE', 
    defaultPrice: 0 
  });

  // États d'édition
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);

  // Charger les données
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [categoriesRes, productsRes] = await Promise.all([
        fetch('/api/categories'),
        fetch('/api/hygiene/products')
      ]);

      if (categoriesRes.ok) {
        const categoriesData = await categoriesRes.json();
        setCategories(categoriesData);
      }
      
      if (productsRes.ok) {
        const productsData = await productsRes.json();
        setProducts(productsData);
      }
      
    } catch (error) {
      setError('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  // INITIALISATION DES CATÉGORIES
  const initializeCategories = async () => {
    try {
      const defaultCategories = [
        { name: 'Hygiène', description: 'Produits d\'hygiène et de nettoyage corporel' },
        { name: 'Nettoyage', description: 'Produits pour l\'entretien des locaux' },
        { name: 'Protection', description: 'Équipements de protection individuelle' },
        { name: 'Bureau', description: 'Fournitures de bureau' }
      ];

      const results = [];
      
      for (const categoryData of defaultCategories) {
        const response = await fetch('/api/categories', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(categoryData)
        });
        
        if (response.ok) {
          const newCategory = await response.json();
          results.push(newCategory);
        }
      }
      
      alert(`${results.length} catégories créées avec succès!`);
      await loadData();
      
    } catch (error) {
      setError('Erreur création catégories: ' + error.message);
    }
  };

  // GESTION DES CATÉGORIES
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/categories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryForm)
      });

      if (response.ok) {
        await loadData();
        setCategoryForm({ name: '', description: '' });
        setError('');
        alert('Catégorie créée avec succès!');
      } else {
        const errorData = await response.json();
        setError(errorData.message);
      }
    } catch (error) {
      setError('Erreur création catégorie: ' + error.message);
    }
  };

  const handleUpdateCategory = async (categoryId) => {
    try {
      const response = await fetch(`/api/categories/${categoryId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryForm)
      });

      if (response.ok) {
        await loadData();
        setEditingCategory(null);
        setCategoryForm({ name: '', description: '' });
        setError('');
        alert('Catégorie modifiée avec succès!');
      } else {
        const errorData = await response.json();
        setError(errorData.message);
      }
    } catch (error) {
      setError('Erreur modification catégorie: ' + error.message);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) return;
    
    try {
      const response = await fetch(`/api/categories/${categoryId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await loadData();
        alert('Catégorie supprimée avec succès!');
      } else {
        const errorData = await response.json();
        setError(errorData.message);
      }
    } catch (error) {
      setError('Erreur suppression catégorie: ' + error.message);
    }
  };

  // GESTION DES PRODUITS
  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/hygiene/products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productForm)
      });

      if (response.ok) {
        await loadData();
        setProductForm({ name: '', category: '', unit: 'PIECE', defaultPrice: 0 });
        setError('');
        alert('Produit créé avec succès!');
      } else {
        const errorData = await response.json();
        setError(errorData.message);
      }
    } catch (error) {
      setError('Erreur création produit: ' + error.message);
    }
  };

  const handleUpdateProduct = async (productId) => {
    try {
      const response = await fetch(`/api/hygiene/products/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productForm)
      });

      if (response.ok) {
        await loadData();
        setEditingProduct(null);
        setProductForm({ name: '', category: '', unit: 'PIECE', defaultPrice: 0 });
        setError('');
        alert('Produit modifié avec succès!');
      } else {
        const errorData = await response.json();
        setError(errorData.message);
      }
    } catch (error) {
      setError('Erreur modification produit: ' + error.message);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) return;
    
    try {
      const response = await fetch(`/api/hygiene/products/${productId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await loadData();
        alert('Produit supprimé avec succès!');
      } else {
        const errorData = await response.json();
        setError(errorData.message);
      }
    } catch (error) {
      setError('Erreur suppression produit: ' + error.message);
    }
  };

  // Fonctions d'édition
  const startEditCategory = (category) => {
    setEditingCategory(category._id);
    setCategoryForm({ 
      name: category.name, 
      description: category.description || '' 
    });
  };

  const startEditProduct = (product) => {
    setEditingProduct(product._id);
    setProductForm({
      name: product.name,
      category: product.category,
      unit: product.unit,
      defaultPrice: product.defaultPrice || 0
    });
  };

  const cancelEdit = () => {
    setEditingCategory(null);
    setEditingProduct(null);
    setCategoryForm({ name: '', description: '' });
    setProductForm({ name: '', category: '', unit: 'PIECE', defaultPrice: 0 });
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="container">
      <h1>Gestion des Catégories et Produits</h1>

      {error && (
        <div className="error-alert">
          <span>{error}</span>
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* BOUTON D'INITIALISATION */}
      {categories.length === 0 && (
        <div className="init-section">
          <h2>Première utilisation</h2>
          <p>Aucune catégorie n'existe. Cliquez pour créer les catégories de base :</p>
          <button 
            onClick={initializeCategories}
            className="btn btn-primary"
          >
            🚀 Initialiser les Catégories de Base
          </button>
        </div>
      )}

      {/* FORMULAIRE CATÉGORIE */}
      <section className="section">
        <div className="section-header">
          <h2>{editingCategory ? 'Modifier une Catégorie' : 'Nouvelle Catégorie'}</h2>
          {editingCategory && (
            <button onClick={cancelEdit} className="btn btn-secondary">
              Annuler
            </button>
          )}
        </div>
        
        <form onSubmit={(e) => editingCategory ? handleUpdateCategory(editingCategory) : handleCreateCategory(e)} className="inline-form">
          <div className="form-row">
            <div className="form-group">
              <label>Nom *</label>
              <input
                type="text"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                required
                placeholder="Nom de la catégorie"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <input
                type="text"
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})}
                placeholder="Description optionnelle"
              />
            </div>
            <div className="form-group">
              <button type="submit" className="btn btn-primary">
                {editingCategory ? 'Modifier' : 'Créer'} Catégorie
              </button>
            </div>
          </div>
        </form>
      </section>

      {/* LISTE DES CATÉGORIES */}
      <section className="section">
        <div className="section-header">
          <h2>Catégories ({categories.length})</h2>
        </div>

        <div className="grid">
          {categories.length === 0 ? (
            <div className="empty-state">
              <p>Aucune catégorie créée</p>
            </div>
          ) : (
            categories.map(category => (
              <div key={category._id} className="card">
                <div className="card-header">
                  <h3>{category.name}</h3>
                  <div className="actions">
                    <button 
                      className="btn-edit"
                      onClick={() => startEditCategory(category)}
                    >
                      ✏️
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={() => handleDeleteCategory(category._id)}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                {category.description && (
                  <p className="card-description">{category.description}</p>
                )}
                <div className="card-footer">
                  {products.filter(p => p.category === category.name).length} produits
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* FORMULAIRE PRODUIT */}
      <section className="section">
        <div className="section-header">
          <h2>{editingProduct ? 'Modifier un Produit' : 'Nouveau Produit'}</h2>
          {editingProduct && (
            <button onClick={cancelEdit} className="btn btn-secondary">
              Annuler
            </button>
          )}
        </div>
        
        <form onSubmit={(e) => editingProduct ? handleUpdateProduct(editingProduct) : handleCreateProduct(e)} className="inline-form">
          <div className="form-row">
            <div className="form-group">
              <label>Nom *</label>
              <input
                type="text"
                value={productForm.name}
                onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                required
                placeholder="Nom du produit"
              />
            </div>
            <div className="form-group">
              <label>Catégorie *</label>
              <select
                value={productForm.category}
                onChange={(e) => setProductForm({...productForm, category: e.target.value})}
                required
                disabled={categories.length === 0}
              >
                <option value="">Sélectionnez une catégorie</option>
                {categories.map(cat => (
                  <option key={cat._id} value={cat.name}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Unité *</label>
              <select
                value={productForm.unit}
                onChange={(e) => setProductForm({...productForm, unit: e.target.value})}
                required
              >
                <option value="PIECE">Pièce</option>
                <option value="LITRE">Litre</option>
                <option value="KG">Kilogramme</option>
              </select>
            </div>
            <div className="form-group">
              <label>Prix</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={productForm.defaultPrice}
                onChange={(e) => setProductForm({...productForm, defaultPrice: parseFloat(e.target.value) || 0})}
                placeholder="0.00"
              />
            </div>
            <div className="form-group">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={categories.length === 0}
              >
                {editingProduct ? 'Modifier' : 'Créer'} Produit
              </button>
            </div>
          </div>
        </form>
      </section>

      {/* LISTE DES PRODUITS */}
      <section className="section">
        <div className="section-header">
          <h2>Produits ({products.length})</h2>
        </div>

        {products.length === 0 ? (
          <div className="empty-state">
            <p>Aucun produit créé</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Catégorie</th>
                  <th>Unité</th>
                  <th>Prix</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map(product => (
                  <tr key={product._id}>
                    <td>{product.name}</td>
                    <td>{product.category}</td>
                    <td>{product.unit}</td>
                    <td>{product.defaultPrice || 0}DT</td>
                    <td>
                      <button 
                        className="btn-edit"
                        onClick={() => startEditProduct(product)}
                      >
                        ✏️
                      </button>
                      <button 
                        className="btn-delete"
                        onClick={() => handleDeleteProduct(product._id)}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default CategoryProductManager;